import React, { useContext, useLayoutEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom'
import { LogoTransfix } from 'transfix-ui/components/LogoTransfix'
import { GlobalToast } from 'transfix-ui/components/Toast'
import { clearUser } from 'utils/actions/user'
import { getApiUrl } from 'utils/api/apiHelpers'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useChatWidgetContext } from 'utils/context/ChatWidgetContext'
import { useFeatureFlag, useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { MarketplaceProvider } from 'utils/context/MarketplaceContext'
import { ValidationFormProvider } from 'utils/context/ValidationFormContext'
import { getInitialNavState } from 'utils/hooks/useNav'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import VerifyPage from 'apps/booking/components/VerifyPage'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { RouteNotFound } from 'components/404'
import { RogersRestfulProvider } from 'components/RogersRestfulProvider'
import { ShareTokenRestfulProvider } from 'components/ShareTokenRestfulProvider'
import { SystemModal } from 'components/SystemModal'
import { useModalState } from 'utils/hooks'
import { FP_HOME, generateFleetPlannerRoutes } from '../config'
import { generateNavRouteGroups } from '../config/newRoutes'
import { onboardingStepRoutes } from '../constants'
// FP Contexts
import {
  ActivationProvider,
  BookableShipmentSavedSearchProvider,
  TeamMembersProvider,
  UserContext,
} from '../context'
import { BookableShipmentAuctionSearchProvider } from '../context/BookableShipmentAuctionSearchContext'
import { BrowserLocationProvider } from '../context/BrowserLocationContext'
import { LaneFiltersProvider } from '../context/LaneFiltersContext'
import { MembershipProvider } from '../context/MembershipContext'
import { SavedLanesProvider } from '../context/SavedLanesContext'
import { TeamMemberFormProvider } from '../context/TeamMemberFormContext'
import { TrackConversionProvider } from '../context/track-conversion-source/TrackConversionContext'
import ChatSupportPage from '../pages/ChatSupportPage'
import AccountDeletionPage from '../pages/account-deletion/AccountDeletionPage'
// FP Pages
import ActivationPage from '../pages/activation/ActivationPage'
// FP Components
import { BidsPage } from '../pages/bids/BidsPage'
import BookLoadsPage from '../pages/book/BookLoadsPages'
import CarrierScorecardPage from '../pages/carrier-scorecard/CarrierScorecardPage'
import { CarriersPage } from '../pages/carriers/CarriersPage'
import { JoinPage } from '../pages/carriers/JoinPage'
import { DedicatedLanesBidPage } from '../pages/dedicated-lanes/DedicatedLanesBidPage'
import { DedicatedLanesMainPage } from '../pages/dedicated-lanes/DedicatedLanesMainPage'
import DetentionRequest from '../pages/detention-request/components/DetentionRequest'
import EquipmentListingPage from '../pages/equipment-listing/EquipmentListingPage'
import CarrierAppHomepage from '../pages/home'
import { IntegrationsPage } from '../pages/integrations/IntegrationsPage'
import { Loads } from '../pages/loads'
import { DeclineOfferPage } from '../pages/loads/DeclineOfferPage'
import { Loads as LoadsV2 } from '../pages/loads_v2/Loads'
import ShipmentsNeedingAttentionPage from '../pages/loads-needing-attention'
import { Login, Password, RequestLink, ResetInviteLink, InvalidInviteLink } from '../pages/login'
import MarketplacePage from '../pages/marketplace/MarketplacePage'
import ShipmentOffersPage from '../pages/offers'
import RateConfirmation from '../pages/rate-confirmation/RateConfirmation'
import { SavedLanesPage } from '../pages/saved-lanes'
import { SharedTruckAvailabiltyPage } from '../pages/saved-searches/SharedTruckAvailabiltyPage'
import { TeamRouterPage } from '../pages/team/TeamRouterPage'
import TrailerDetailPage from '../pages/trailer-detail/TrailerDetailPage'
import { UserSettingsIndexPage } from '../pages/user/UserSettingsIndexPage'
import { AppWrap, AppWrapNavVariants } from './AppWrap'
import DashboardPage from './Dashboard/DashboardPage'
import { PrivateRoute } from './PrivateRoute'
import { RunItAgainPage } from './bookable-shipment-saved-search/pages/RunItAgainPage'
import { TruckAvailabiltyPage } from './bookable-shipment-saved-search/pages/TruckAvailabiltyPage'
import { DriverValidationExpiredPage } from './driver-validation/DriverValidationExpiredPage'
import { DriverValidationPage } from './driver-validation/DriverValidationPage'
import { DriverVerificationExpiredPage } from './driver-validation/DriverVerificationExpiredPage'
import { DriverVerificationPage } from './driver-validation/DriverVerificationPage'
import { DriversPage } from './drivers/DriversPage'
import { ExternalLoad } from './external-load/ExternalLoad'
import { FacilitiesPage } from './facilities/FacilitiesPage'
import FleetAsync from './fleet/Fleet'
import { LoadDetails } from './load-details'
import { SupportModal } from './modals'
import { OnboardingWizardPage } from './onboarding-wizard/OnboardingWizardPage'
import ShareLoadDetails from './share-load/ShareLoadDetails'
import { ShareLoadDetailsExpired } from './share-load/ShareLoadDetailsExpired'
import { QuickBooksCallback } from './user/QuickbooksCallback'
import { QuickBooksConnect } from './user/QuickbooksConnect'
import '../scss/main.scss'
import '../scss/pages/_onboarding.scss'

export const APP_NAME = 'fms'

const LOAD_DETAILS_MODAL_PATHS =
  'edit|delete|share|share-load-via-sms|sms-prompt-settings|update-status|update-eta|tracking-method|automated-status-updates|post-truck'

const useHideSidebar = () => {
  return !!useRouteMatch(['/fms/activate/:id', '/fms/account-deletion'])
}

const EquipmentListingPages = [
  <PrivateRoute key='equipment' exact path={['/equipment']}>
    <EquipmentListingPage />
  </PrivateRoute>,
  <PrivateRoute key='equipment-trailer' exact path='/equipment/trailer/:motorCarrierId/:id'>
    <TrailerDetailPage />
  </PrivateRoute>,
]

export function App() {
  const { trackEvent } = useFMSAnalytics()
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isSideNavEnabled } = getFlag('fmsSideNav')
  const { enabled: isDedicatedLanesPageEnabled } = getFlag('dedicatedLanesPage')
  const { enabled: isCarrierScorecardPageEnabled } = getFlag('carrierScorecardPage')
  const { enabled: isEquipmentListingPageEnabled } = getFlag('fmsEquipmentListingPage')
  const { enabled: isCarriersPageDisabled } = getFlag('motorCarrierProfileHideFmsNav')
  const { enabled: isNewHomepageFlagEnabled } = getFlag('enableNewCarrierAppHomepage')
  const { enabled: isNewHomepageEnabledForAllCarriers } = getFlag('enableCarrierHomepageForAll')
  const { enabled: allowNewMyLoadsTable } = getFlag('allowNewMyLoadsTable')

  const isAccountDeletionEnabled = useFeatureFlag('fmsAccountDeletion')

  const { clearUserData, hasLoadedUser, userData, userFlags } = useContext(UserContext)

  const showCarrierScorecardPage =
    isCarrierScorecardPageEnabled && userData?.user?.hasCarrierScorecardEnabled
  const showEquipmentListingPage =
    isEquipmentListingPageEnabled && userData?.user?.hasDropTrailerEnabled

  const { boot, shutdown } = useChatWidgetContext()
  const dispatch = useDispatch()
  const isSidebarHidden = useHideSidebar()
  const { logout, userDetails, userPermissions } = useUserDetails()
  const { isMobile } = useBreakpointContext()
  const isNavExpanded = isMobile ? false : getInitialNavState()
  const { id: authUserId, inCarrierHomepageCohort } = userDetails || {}

  const onLogoutHandler = async () => {
    trackEvent('Login', 'LOGGED_OUT')
    dispatch(clearUser)
    await clearUserData()
    shutdown(true)
    logout({ origin: 'fms' })
  }

  const showSideBar = useMemo(
    () => !!authUserId && hasLoadedUser && userFlags.onboardingHasAddedCarriers && !isSidebarHidden,
    [authUserId, hasLoadedUser, userFlags.onboardingHasAddedCarriers, isSidebarHidden]
  )

  // Mount the chat widget. Hide the widget on mobile
  useLayoutEffect(() => {
    const showChatWidget = !isMobile
    boot({
      useDefaultLauncher: showChatWidget,
    })
  }, [])

  /**
   * Used to manage the multiple modal states
   */
  const { createToggleHandler, modalState, toggleModal } = useModalState({
    support: false,
    featureFlags: false,
  })

  // Configure the side nav for CP <> FP
  const { primary, secondary } = generateFleetPlannerRoutes(userPermissions)
  const navRouteGroups = generateNavRouteGroups({
    permissions: userPermissions,
    context: {
      onLogOut: onLogoutHandler,
      onToggleModal: toggleModal,
      isDedicatedLanesPageEnabled,
      showCarrierScorecardPage,
      isEquipmentListingPageEnabled: showEquipmentListingPage,
      isCarriersPageEnabled: !isCarriersPageDisabled,
    },
  })

  const supportModalHandler = createToggleHandler('support')
  const featureFlagModalHandler = createToggleHandler('featureFlags')

  const appWrapVariant = showSideBar ? AppWrapNavVariants.side : AppWrapNavVariants.none

  const isNewHomepageEnabled =
    (isNewHomepageFlagEnabled && inCarrierHomepageCohort) || isNewHomepageEnabledForAllCarriers

  return (
    <AppWrap
      appName={APP_NAME}
      title='Transfix Carrier App'
      variant={appWrapVariant}
      routes={primary}
      secondaryRoutes={secondary}
      homeRoute={FP_HOME}
      logo={<LogoTransfix color='white' />}
      onSupport={supportModalHandler}
      navExpanded={isNavExpanded}
      persistNavBar
      isNewSideNavEnabled={isSideNavEnabled}
      navRouteGroups={navRouteGroups}
      onLogout={onLogoutHandler}>
      <GlobalToast />
      <TrackConversionProvider>
        <Switch>
          <PrivateRoute exact path='/activate/:id'>
            <ActivationProvider>
              <ActivationPage />
            </ActivationProvider>
          </PrivateRoute>
          <Route exact path='/onboarding/:childPath?'>
            <Redirect to='/new-onboarding' />
          </Route>
          <PrivateRoute
            requiresOnboarding
            exact
            path={[
              '/schedule/:range?/:date?/:id?/:action(edit|delete|share)?',
              '/schedule/:range/:date/create-load',
            ]}>
            <MarketplaceProvider>
              <ValidationFormProvider>
                <FleetAsync />
              </ValidationFormProvider>
            </MarketplaceProvider>
          </PrivateRoute>
          <PrivateRoute
            requiresOnboarding
            exact
            path={[
              `/loads/:id?/:action(${LOAD_DETAILS_MODAL_PATHS})?`,
              '/loads/:action(update-status|update-eta)?',
              '/loads/create-load',
              '/loads/not-found',
            ]}>
            <BookableShipmentAuctionSearchProvider>
              <MembershipProvider>
                <ValidationFormProvider>
                  {allowNewMyLoadsTable ? <LoadsV2 /> : <Loads />}
                </ValidationFormProvider>
              </MembershipProvider>
            </BookableShipmentAuctionSearchProvider>
          </PrivateRoute>
          <PrivateRoute
            requiresOnboarding
            exact
            path={[`/load/:id/:action(${LOAD_DETAILS_MODAL_PATHS})?`, '/load/not-found']}>
            <MembershipProvider>
              <MarketplaceProvider>
                <LoadDetails />
              </MarketplaceProvider>
            </MembershipProvider>
          </PrivateRoute>
          <PrivateRoute requiresOnboarding exact path='/external_load_sync/:externalLoadId'>
            <ExternalLoad />
          </PrivateRoute>

          <PrivateRoute requiresOnboarding path='/user/settings'>
            <MembershipProvider>
              <SavedLanesProvider>
                <UserSettingsIndexPage />
              </SavedLanesProvider>
            </MembershipProvider>
          </PrivateRoute>

          <PrivateRoute requiresOnboarding path='/integrations'>
            <MembershipProvider>
              <IntegrationsPage />
            </MembershipProvider>
          </PrivateRoute>
          <PrivateRoute path={['/transfix-lanes', '/transfix-lanes/add-alert']}>
            <SavedLanesProvider>
              <SavedLanesPage />
            </SavedLanesProvider>
          </PrivateRoute>
          <PrivateRoute path='/truck-availability'>
            <BookableShipmentSavedSearchProvider>
              <LaneFiltersProvider>
                <TruckAvailabiltyPage />
              </LaneFiltersProvider>
            </BookableShipmentSavedSearchProvider>
          </PrivateRoute>
          <PrivateRoute
            requiresOnboarding
            exact
            path={[
              '/bids',
              '/bids/:shipmentId/',
              '/bids/:shipmentId/bid',
              '/bids/:shipmentId/bid/:bidId',
              '/bids/:shipmentId?/compliance',
            ]}>
            <MembershipProvider>
              <MarketplaceProvider>
                <BidsPage />
              </MarketplaceProvider>
            </MembershipProvider>
          </PrivateRoute>
          <PrivateRoute
            requiresOnboarding
            exact
            path={[
              '/historical-lanes',
              '/historical-lanes/:shipmentId/',
              '/historical-lanes/:shipmentId/bid',
              '/historical-lanes/:shipmentId/bid/:bidId',
              '/historical-lanes/:shipmentId?/compliance',
              '/historical-lanes/:shipmentId/bid_accepted',
            ]}>
            <BookableShipmentSavedSearchProvider>
              <MembershipProvider>
                <MarketplaceProvider>
                  <LaneFiltersProvider>
                    <RunItAgainPage />
                  </LaneFiltersProvider>
                </MarketplaceProvider>
              </MembershipProvider>
            </BookableShipmentSavedSearchProvider>
          </PrivateRoute>
          <PrivateRoute requiresOnboarding path='/team'>
            <TeamMembersProvider>
              <TeamMemberFormProvider>
                <TeamRouterPage />
              </TeamMemberFormProvider>
            </TeamMembersProvider>
          </PrivateRoute>
          <PrivateRoute requiresOnboarding exact path={['/carriers', '/carriers/add']}>
            <CarriersPage />
          </PrivateRoute>
          <Route exact path='/carriers/join/:joinId'>
            <JoinPage />
          </Route>
          <PrivateRoute requiresOnboarding exact path={['/qb-connect']}>
            <QuickBooksConnect />
          </PrivateRoute>
          <Route exact path={[...onboardingStepRoutes]}>
            <OnboardingWizardPage />
          </Route>
          {isAccountDeletionEnabled && (
            <PrivateRoute path='/account-deletion'>
              <AccountDeletionPage />
            </PrivateRoute>
          )}
          <PrivateRoute
            requiresOnboarding
            exact
            path={[
              '/book/:shipmentId/',
              '/book/:shipmentId/bid',
              '/book/:shipmentId/bid/:bidId',
              '/book/:shipmentId?/compliance',
              '/book/:shipmentId/bid_accepted',
              '/book',
            ]}>
            <LaneFiltersProvider>
              <MembershipProvider>
                <MarketplaceProvider>
                  <SavedLanesProvider>
                    <BrowserLocationProvider>
                      <BookLoadsPage />
                    </BrowserLocationProvider>
                  </SavedLanesProvider>
                </MarketplaceProvider>
              </MembershipProvider>
            </LaneFiltersProvider>
          </PrivateRoute>
          <PrivateRoute
            requiresOnboarding
            exact
            path={[
              '/book/:laneId/lane-details',
              '/book/:laneId/lane-details/bid',
              '/book/:laneId/lane-details/interest',
            ]}>
            <MembershipProvider>
              <MarketplaceProvider>
                {isMobile ? <DedicatedLanesBidPage /> : <MarketplacePage />}
              </MarketplaceProvider>
            </MembershipProvider>
          </PrivateRoute>
          <PrivateRoute requiresOnboarding exact path='/book/:id/rate-confirmation'>
            <MembershipProvider>
              <MarketplaceProvider>
                <RateConfirmation />
              </MarketplaceProvider>
            </MembershipProvider>
          </PrivateRoute>
          <PrivateRoute
            requiresOnboarding
            exact
            path={['/detention-request/:shipmentId', '/detention-request/:shipmentId/success']}>
            <MembershipProvider>
              <MarketplaceProvider>
                <DetentionRequest />
              </MarketplaceProvider>
            </MembershipProvider>
          </PrivateRoute>
          <PrivateRoute requiresOnboarding exact path={['/dedicated-lanes/']}>
            <Redirect exact to='/dedicated-lanes/search' />
            <DedicatedLanesMainPage />
          </PrivateRoute>
          <PrivateRoute
            requiresOnboarding
            exact
            path={['/dedicated-lanes/:tab', '/dedicated-lanes/contracts/:id']}>
            <DedicatedLanesMainPage />
          </PrivateRoute>
          <PrivateRoute requiresOnboarding exact path={['/dedicated-lanes/lane/:laneId']}>
            <DedicatedLanesBidPage />
          </PrivateRoute>
          <PrivateRoute requiresOnboarding exact path={['/offers', '/offers/:shipmentId']}>
            <MembershipProvider>
              <MarketplaceProvider>
                <ShipmentOffersPage />
              </MarketplaceProvider>
            </MembershipProvider>
          </PrivateRoute>
          <Route exact path='/decline/:shipmentId'>
            <DeclineOfferPage />
          </Route>
          <PrivateRoute
            requiresOnboarding
            exact
            path={[
              '/home',
              `/home/:shipmentId/details/:action(${LOAD_DETAILS_MODAL_PATHS})?`,
              '/home/:shipmentId/book',
              '/home/:shipmentId/decline',
              '/home/carrier-setup',
            ]}>
            <ActivationProvider>
              <MembershipProvider>
                <MarketplaceProvider>
                  {isNewHomepageEnabled ? <CarrierAppHomepage /> : <DashboardPage />}
                </MarketplaceProvider>
              </MembershipProvider>
            </ActivationProvider>
          </PrivateRoute>
          <PrivateRoute
            requiresOnboarding
            exact
            path={[
              '/loads-needing-attention',
              `/loads-needing-attention/:shipmentId/details/:action(${LOAD_DETAILS_MODAL_PATHS})?`,
            ]}>
            <MarketplaceProvider>
              <ShipmentsNeedingAttentionPage />
            </MarketplaceProvider>
          </PrivateRoute>
          <PrivateRoute
            requiresOnboarding
            exact
            path={['/drivers/create-driver', '/drivers/edit-driver/:id?', '/drivers']}>
            <MembershipProvider>
              <ValidationFormProvider>
                <DriversPage />
              </ValidationFormProvider>
            </MembershipProvider>
          </PrivateRoute>
          <PrivateRoute
            requiresOnboarding
            exact
            path={['/facilities/create-facility', '/facilities/edit-facility/:id?', '/facilities']}>
            <ValidationFormProvider>
              <FacilitiesPage />
            </ValidationFormProvider>
          </PrivateRoute>
          {showCarrierScorecardPage && (
            <PrivateRoute requiresOnboarding exact path={['/carrier-scorecard']}>
              <CarrierScorecardPage />
            </PrivateRoute>
          )}
          <Route path={['/qb-callback']}>
            <QuickBooksCallback />
          </Route>
          <Route
            exact
            path={['/share-load/details/:id/:token', '/share-load-external/details/:id/:token']}>
            <MarketplaceProvider>
              <ShareLoadDetails />
            </MarketplaceProvider>
          </Route>
          <Route exact path='/shared-truck-availability/:uuid'>
            <SharedTruckAvailabiltyPage />
          </Route>
          <Route exact path='/share-load/expired'>
            <ShareLoadDetailsExpired />
          </Route>
          <Route exact path='/securebooking/:token'>
            <ShareTokenRestfulProvider
              onUnauthenticatedResponse={() => {}}
              baseApi={`${getApiUrl('FMS')}/v1`}>
              <DriverValidationPage />
            </ShareTokenRestfulProvider>
          </Route>
          <Route exact path='/securebooking-expired'>
            <DriverValidationExpiredPage />
          </Route>
          <Route exact path='/verify-driver/:token'>
            <ShareTokenRestfulProvider
              onUnauthenticatedResponse={() => {}}
              baseApi={`${getApiUrl('FMS')}/v1`}>
              <DriverVerificationPage />
            </ShareTokenRestfulProvider>
          </Route>
          <Route exact path='/verify-driver-expired'>
            <DriverVerificationExpiredPage />
          </Route>
          {showEquipmentListingPage && EquipmentListingPages}
          <Route exact path='/login/request_link'>
            <RequestLink />
          </Route>
          <Route exact path='/login/:token?'>
            <Login />
          </Route>
          <Route exact path='/login/password/:token'>
            <Password />
          </Route>
          <Route exact path='/login/invite/:token'>
            <Password invite />
          </Route>
          <Route exact path='/login/invite/:token/reset'>
            <ResetInviteLink />
          </Route>
          <Route exact path='/login/invite/:token/invalid'>
            <InvalidInviteLink />
          </Route>
          <Route path='/verify'>
            <RogersRestfulProvider
              baseApi={getApiUrl('MARKETPLACE')}
              onUnauthenticatedResponse={() => {}}>
              <VerifyPage />
            </RogersRestfulProvider>
          </Route>
          <Route path='/support-chat'>
            <ChatSupportPage />
          </Route>
          <Route exact path='/'>
            <Redirect to={FP_HOME} />
          </Route>
          <Route>
            <RouteNotFound />
          </Route>
        </Switch>
      </TrackConversionProvider>
      <SupportModal isOpen={modalState.support} onClose={supportModalHandler} />
      {modalState.featureFlags && (
        <SystemModal open={modalState.featureFlags} close={featureFlagModalHandler} />
      )}
    </AppWrap>
  )
}
